// Maps each production domain to the hreflang codes it should be advertised under.
// Adding a new regional domain only requires extending this map.
export const REGIONAL_HREFLANGS: Record<string, readonly string[]> = {
  "www.peoplematters.in": ["en-IN"],
  "anz.peoplemattersglobal.com": ["en-AU", "en-NZ"],
  "sea.peoplemattersglobal.com": ["en-SG", "en-MY", "en-PH", "en-ID", "en-VN", "en-TH", "en-KH", "en-LA"],
  "me.peoplemattersglobal.com": ["en-AE", "en-SA", "en-QA", "en-KW", "en-OM", "en-BH"],
};

// Given the list of hostnames an article is published on (from
// articleData.enabled_hosts), returns the subset that are production
// regional hosts.
//
// Returns undefined when:
// - the enabled list is missing/empty (treat as "unknown" — emit hreflang
//   for all production hosts as a safe fallback), or
// - none of the entries map to a known production host.
//
// Callers pass the result as `allowedHosts` to buildRegionalAlternates;
// undefined falls through to "no gating" (all hosts emitted).
export function enabledHostsToAllowedHosts(
  enabledHosts: Array<string | null | undefined> | null | undefined
): string[] | undefined {
  if (!enabledHosts || enabledHosts.length === 0) return undefined;
  const allowed = new Set<string>();
  for (const raw of enabledHosts) {
    const host = raw?.trim().toLowerCase();
    if (host && isRegionalProductionHost(host)) {
      allowed.add(host);
    }
  }
  return allowed.size > 0 ? Array.from(allowed) : undefined;
}

// Fallback x-default host if the article's origin domain can't be determined
// or doesn't match one of our production hosts.
export const DEFAULT_REGIONAL_HOST = "www.peoplematters.in";

const PRODUCTION_HOSTS = new Set(Object.keys(REGIONAL_HREFLANGS));

export function isRegionalProductionHost(host: string): boolean {
  return PRODUCTION_HOSTS.has(host.toLowerCase());
}

// Returns the primary hreflang code (e.g. "en-IN") for a production host,
// used as inLanguage on JSON-LD schemas so they agree with hreflang.
// Returns undefined for non-production hosts.
export function getPrimaryHreflangForHost(host: string): string | undefined {
  return REGIONAL_HREFLANGS[host.toLowerCase()]?.[0];
}

function normalizePath(path: string): string {
  if (!path) return "/";
  return path.startsWith("/") ? path : `/${path}`;
}

function extractHost(url: string | undefined | null): string | null {
  if (!url) return null;
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return null;
  }
}

export type RegionalAlternates = {
  canonical: string;
  languages?: Record<string, string>;
};

// Builds a self-referencing canonical and a full hreflang map for the
// current request. Non-production hosts (uat/beta/qa/local) get a canonical
// but no hreflang, so test environments never leak into search indexes.
//
// path: path used for the canonical URL.
// hreflangPath: path used for hreflang URLs and x-default. Defaults to
//   `path`. AMP routes pass "/amp" + canonical-path here so the AMP page's
//   hreflang points at AMP variants of each regional domain, while its
//   canonical still points at the non-AMP self URL.
// originUrl: optional URL whose hostname identifies the article's origin
// domain. Used to choose the x-default. If absent or not one of our
// production hosts, DEFAULT_REGIONAL_HOST is used.
// allowedHosts: optional list of production hosts where this article is
// available. When non-empty, hreflang is emitted only for those hosts
// (plus the current host, to keep canonical/hreflang in sync). When
// undefined or empty, all production hosts are emitted — so a missing or
// unmapped countries field degrades safely to current behavior.
export function buildRegionalAlternates({
  currentHost,
  path,
  hreflangPath,
  originUrl,
  allowedHosts,
}: {
  currentHost: string;
  path: string;
  hreflangPath?: string;
  originUrl?: string | null;
  allowedHosts?: string[];
}): RegionalAlternates {
  const normalizedCanonicalPath = normalizePath(path);
  const normalizedHreflangPath = normalizePath(hreflangPath ?? path);
  const host = currentHost.toLowerCase();
  const canonical = `https://${host}${normalizedCanonicalPath}`;

  if (!isRegionalProductionHost(host)) {
    return { canonical };
  }

  const gatingActive = Array.isArray(allowedHosts) && allowedHosts.length > 0;
  const effectiveAllowed = gatingActive
    ? new Set<string>([...allowedHosts!, host])
    : null;

  const languages: Record<string, string> = {};
  for (const [regionalHost, hreflangs] of Object.entries(REGIONAL_HREFLANGS)) {
    if (effectiveAllowed && !effectiveAllowed.has(regionalHost)) continue;
    const url = `https://${regionalHost}${normalizedHreflangPath}`;
    for (const lang of hreflangs) {
      languages[lang] = url;
    }
  }

  const parsedOriginHost = extractHost(originUrl);
  const originAllowed =
    parsedOriginHost &&
    PRODUCTION_HOSTS.has(parsedOriginHost) &&
    (!effectiveAllowed || effectiveAllowed.has(parsedOriginHost));
  const fallbackHost =
    effectiveAllowed && !effectiveAllowed.has(DEFAULT_REGIONAL_HOST)
      ? Array.from(effectiveAllowed)[0]
      : DEFAULT_REGIONAL_HOST;
  const xDefaultHost = originAllowed ? parsedOriginHost! : fallbackHost;
  languages["x-default"] = `https://${xDefaultHost}${normalizedHreflangPath}`;

  return { canonical, languages };
}
